import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash, FaShieldAlt, FaKey } from "react-icons/fa";

const API_URL = "https://awriter-backend-si7g.onrender.com";

const Login = () => {
  const navigate = useNavigate();
  const [isFlipped, setIsFlipped] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [showBackupCode, setShowBackupCode] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: localStorage.getItem("rememberedEmail") || "",
    password: "",
    twoFactorToken: "",
    backupCode: "",
  });

  const [signUpData, setSignUpData] = useState({
    email: "",
    password: "",
    adminSecret: "",
  });

  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(!!localStorage.getItem("rememberedEmail"));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    setError("");
    setSuccessMessage("");
    setShow2FA(false);
    setShowBackupCode(false);
    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setFormData({
      email: localStorage.getItem("rememberedEmail") || "",
      password: "",
      twoFactorToken: "",
      backupCode: "",
    });
    setSignUpData({
      email: "",
      password: "",
      adminSecret: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (isFlipped) {
      setSignUpData({ ...signUpData, [name]: value });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handle2FAToggle = () => {
    setShowBackupCode(!showBackupCode);
    setFormData({ ...formData, twoFactorToken: "", backupCode: "" });
    setError("");
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      console.log("Sending forgot password request:", { email: forgotPasswordEmail });
      const response = await axios.post(`${API_URL}/api/admin/forgot-password`, {
        email: forgotPasswordEmail,
      });
      console.log("Forgot password response:", response.data);
      setSuccessMessage("Password reset link sent to your email.");
      toast.success("Password reset link sent to your email.");
      setTimeout(() => {
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
        setSuccessMessage("");
      }, 2000);
    } catch (err) {
      console.error("Forgot password error:", err.response?.data || err.message);
      const errorMessage = err.response?.data?.error || "Failed to send password reset link.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const url = isFlipped
        ? `${API_URL}/api/admin/register`
        : `${API_URL}/api/admin/signin`;

      const data = isFlipped
        ? signUpData
        : {
            email: formData.email,
            password: formData.password,
            ...(show2FA && {
              twoFactorToken: showBackupCode ? null : formData.twoFactorToken,
              backupCode: showBackupCode ? formData.backupCode : null,
            }),
          };

      console.log(`Sending ${isFlipped ? "admin signup" : "login"} request:`, {
        ...data,
        password: "[HIDDEN]",
        adminSecret: data.adminSecret ? "[HIDDEN]" : undefined,
      });

      const response = await axios.post(url, data);
      console.log(`${isFlipped ? "Admin Signup" : "Login"} response:`, {
        ...response.data,
        token: response.data.token ? "[HIDDEN]" : undefined,
      });

      if (isFlipped) {
        if (response.status === 201 && response.data.admin) {
          setSuccessMessage("Admin account created! Awaiting superadmin approval.");
          toast.success("Admin account created! Awaiting superadmin approval.");
          setTimeout(() => {
            setIsFlipped(false);
            setSuccessMessage("");
            setSignUpData({ email: "", password: "", adminSecret: "" });
          }, 2000);
        } else {
          throw new Error("Unexpected response format");
        }
      } else {
        if (response.data.token) {
          localStorage.setItem("adminToken", response.data.token);
          localStorage.setItem("tokenTimestamp", Date.now());
          if (response.data.admin) {
            localStorage.setItem("adminData", JSON.stringify(response.data.admin));
          }
          if (rememberMe) {
            localStorage.setItem("rememberedEmail", data.email);
          } else {
            localStorage.removeItem("rememberedEmail");
          }
          navigate("/dashboard");
        } else {
          throw new Error("No token received");
        }
      }
    } catch (err) {
      console.error(`${isFlipped ? "Admin Signup" : "Login"} error:`, err.response?.data || err.message);
      let errorMessage = `Failed to ${isFlipped ? "sign up" : "login"}`;
      if (err.response) {
        const { status, data } = err.response;
        if (status === 401 && data.requires2FA) {
          setShow2FA(true);
          setError("Two-Factor Authentication required");
          setLoading(false);
          return;
        }
        switch (status) {
          case 400:
            errorMessage = data.error || "Email, password, and admin secret key required";
            break;
          case 401:
            errorMessage = data.error || "Invalid credentials or admin secret key";
            break;
          case 403:
            errorMessage = data.error || "Account is blocked";
            break;
          case 404:
            errorMessage = isFlipped ? "Invalid request" : "Admin not found";
            break;
          case 409:
            errorMessage = "Email already in use";
            break;
          default:
            errorMessage = data.error || "Server error";
        }
      } else if (err.message === "Network Error") {
        errorMessage = "Network connection failed.";
      }
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full flex flex-col md:flex-row justify-center items-center min-h-screen overflow-hidden">
      <div
        className={`bg-[#D1CBCD] w-full md:w-1/2 h-screen transition-transform duration-700 ease-in-out hidden md:block ${
          isFlipped ? "translate-x-full" : "translate-x-0"
        }`}
      >
        <div className="flex items-center justify-center h-full">
          <img src="/assets/undraw_typewriter_d4km 1.png" alt="Illustration" />
        </div>
      </div>
      <div
        className={`w-full md:w-1/2 h-screen m-auto flex justify-center items-center transition-transform duration-500 ease-in-out ${
          isFlipped ? "md:-translate-x-full" : "translate-x-0"
        }`}
      >
        {showForgotPassword ? (
          <form onSubmit={handleForgotPasswordSubmit} className="flex flex-col w-[90%] md:w-[70%] space-y-5 p-4">
            <h1 className="text-4xl md:text-6xl mb-14">Reset Password</h1>
            <input
              type="email"
              name="email"
              className="border-b-2 pb-2 outline-none"
              placeholder="Enter your email"
              value={forgotPasswordEmail}
              onChange={(e) => setForgotPasswordEmail(e.target.value)}
              required
            />
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-600 text-sm">{successMessage}</p>
              </div>
            )}
            <div className="w-full md:w-[80%] space-y-5 mx-auto">
              <button
                type="submit"
                className="bg-[#D44D76] text-white mt-5 p-2 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </div>
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotPasswordEmail("");
                  setError("");
                  setSuccessMessage("");
                }}
                className="text-sm text-gray-600 hover:underline"
              >
                ← Back to login
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col w-[90%] md:w-[70%] space-y-5 p-4">
            <h1 className="text-4xl md:text-6xl mb-14">
              {isFlipped ? "Create Admin Account" : "Welcome Back"}
            </h1>
            <input
              type="email"
              name="email"
              className="border-b-2 pb-2 outline-none"
              placeholder="Email"
              value={isFlipped ? signUpData.email : formData.email}
              onChange={handleChange}
              required
              disabled={show2FA && !isFlipped}
            />
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                className="border-b-2 pb-2 outline-none w-full"
                placeholder="Password"
                value={isFlipped ? signUpData.password : formData.password}
                onChange={handleChange}
                required
                disabled={show2FA && !isFlipped}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {isFlipped && (
              <input
                type="text"
                name="adminSecret"
                className="border-b-2 pb-2 outline-none"
                placeholder="Admin Secret Key"
                value={signUpData.adminSecret}
                onChange={handleChange}
                required
              />
            )}
            {show2FA && !isFlipped && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2 text-blue-600 font-medium">
                  <FaShieldAlt />
                  <span>Two-Factor Authentication</span>
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  Enter your 6-digit authentication code or a backup code.
                </div>
                {!showBackupCode ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      name="twoFactorToken"
                      className="border-b-2 pb-2 outline-none w-full text-center text-2xl tracking-widest"
                      placeholder="000000"
                      value={formData.twoFactorToken}
                      onChange={handleChange}
                      maxLength="6"
                      pattern="[0-9]{6}"
                      autoComplete="off"
                      required
                    />
                    <button
                      type="button"
                      onClick={handle2FAToggle}
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <FaKey size={12} />
                      Use backup code
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <input
                      type="text"
                      name="backupCode"
                      className="border-b-2 pb-2 outline-none w-full text-center text-lg tracking-wider"
                      placeholder="Enter backup code"
                      value={formData.backupCode}
                      onChange={handleChange}
                      maxLength="8"
                      autoComplete="off"
                      required
                    />
                    <button
                      type="button"
                      onClick={handle2FAToggle}
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <FaShieldAlt size={12} />
                      Use authenticator code
                    </button>
                  </div>
                )}
              </div>
            )}
            {!isFlipped && !show2FA && (
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe(!rememberMe)}
                  />
                  Remember Me
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setError("");
                    setSuccessMessage("");
                  }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
            )}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
            {successMessage && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-600 text-sm">{successMessage}</p>
              </div>
            )}
            <div className="w-full md:w-[80%] space-y-5 mx-auto">
              <button
                type="submit"
                className="bg-[#D44D76] text-white mt-5 p-2 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : show2FA ? (
                  "Verify & Login"
                ) : isFlipped ? (
                  "Sign Up"
                ) : (
                  "Login"
                )}
              </button>
            </div>
            {!show2FA && !showForgotPassword && (
              <div>
                <h1 className="mt-10 text-lg text-center text-[#0000006c]">
                  {isFlipped ? "Already have an Account?" : "Don't have an Account?"}
                  <span
                    className="text-black ml-1 cursor-pointer hover:underline"
                    onClick={handleFlip}
                  >
                    {isFlipped ? "Sign In" : "Sign Up"}
                  </span>
                </h1>
              </div>
            )}
            {show2FA && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShow2FA(false);
                    setShowBackupCode(false);
                    setFormData({ ...formData, twoFactorToken: "", backupCode: "" });
                    setError("");
                  }}
                  className="text-sm text-gray-600 hover:underline"
                >
                  ← Back to login
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;